<template>
  <div class="step-card">

    <!-- Header -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="M12 3L14 8L19 10L14 12L12 17L10 12L5 10L10 8L12 3Z"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />

          <path
            d="M18 15L19 18L22 19L19 20L18 23L17 20L14 19L17 18L18 15Z"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Позиционирование
        </span>

        <h3>
          Расскажите о компании
        </h3>
      </div>
    </div>

    <p class="description">
      Укажите специализацию компании и коротко расскажите,
      чем занимается команда, какие задачи решает и в чём
      заключаются её сильные стороны.
    </p>

    <div class="form-content">

      <!-- Специфика компании -->
      <div class="field-block">
        <div class="field-heading">
          <label
            class="field-label"
            for="company-specifics"
          >
            Специфика компании
          </label>

          <span class="character-counter">
            {{ specificsLength }}/120
          </span>
        </div>

        <div
          class="input-wrapper"
          :class="{
            invalid:
              specificsTouched &&
              !specificsValid,

            valid:
              specificsTouched &&
              specificsValid
          }"
        >
          <div class="field-icon">
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M4 6H20M4 12H15M4 18H11"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <input
            id="company-specifics"
            v-model="model.specifics"
            type="text"
            class="text-input"
            placeholder="Например: Маркетинг, видеопродакшн"
            maxlength="120"
            autocomplete="off"
            @input="specificsTouched = true"
            @blur="specificsTouched = true"
          />

          <transition name="status">
            <div
              v-if="
                specificsTouched &&
                specificsValid
              "
              class="valid-status"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5L10 17L19 7"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </transition>
        </div>

        <transition name="message" mode="out-in">
          <p
            v-if="
              specificsTouched &&
              !specificsValid
            "
            key="specifics-error"
            class="field-message error"
          >
            <span class="message-icon">!</span>

            Укажите специализацию компании
          </p>

          <p
            v-else
            key="specifics-hint"
            class="field-message hint"
          >
            Можно указать несколько направлений через запятую.
          </p>
        </transition>
      </div>

      <!-- Описание компании -->
      <div class="field-block">
        <div class="field-heading">
          <label
            class="field-label"
            for="company-description"
          >
            Описание компании
          </label>

          <span class="character-counter">
            {{ descriptionLength }}/1500
          </span>
        </div>

        <div
          class="textarea-wrapper"
          :class="{
            invalid:
              descriptionTouched &&
              !descriptionValid,

            valid:
              descriptionTouched &&
              descriptionValid
          }"
        >
          <textarea
            id="company-description"
            v-model="model.aboutCompany"
            class="textarea"
            placeholder="Расскажите о компании, команде, продуктах и основных направлениях работы..."
            maxlength="1500"
            @input="descriptionTouched = true"
            @blur="descriptionTouched = true"
          ></textarea>

          <transition name="status">
            <div
              v-if="
                descriptionTouched &&
                descriptionValid
              "
              class="textarea-valid-status"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5L10 17L19 7"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </transition>
        </div>

        <transition name="message" mode="out-in">
          <p
            v-if="
              descriptionTouched &&
              !descriptionValid
            "
            key="description-error"
            class="field-message error"
          >
            <span class="message-icon">!</span>

            Добавьте небольшое описание компании
          </p>

          <p
            v-else
            key="description-hint"
            class="field-message hint"
          >
            Хорошее описание помогает кандидатам быстрее понять
            культуру и направление компании.
          </p>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from "vue";

const model = defineModel({
  required: true
});

const emit = defineEmits([
  "valid",
  "not-valid"
]);

// MARK: - Initial values

if (
  typeof model.value.specifics !==
  "string"
) {
  model.value.specifics = "";
}

if (
  typeof model.value.aboutCompany !==
  "string"
) {
  model.value.aboutCompany = "";
}

// MARK: - State

const specificsTouched = ref(
  model.value.specifics.trim().length > 0
);

const descriptionTouched = ref(
  model.value.aboutCompany.trim().length > 0
);

// MARK: - Computed

const normalizedSpecifics = computed(() => {
  return model.value.specifics.trim();
});

const normalizedDescription = computed(() => {
  return model.value.aboutCompany.trim();
});

const specificsLength = computed(() => {
  return model.value.specifics.length;
});

const descriptionLength = computed(() => {
  return model.value.aboutCompany.length;
});

const specificsValid = computed(() => {
  return normalizedSpecifics.value.length >= 2;
});

const descriptionValid = computed(() => {
  return normalizedDescription.value.length >= 5;
});

const isValid = computed(() => {
  return (
    specificsValid.value &&
    descriptionValid.value
  );
});

// MARK: - Validation

watch(
  [
    () => model.value.specifics,
    () => model.value.aboutCompany
  ],

  () => {
    if (isValid.value) {
      emit("valid");
    } else {
      emit("not-valid");
    }
  },

  {
    immediate: true
  }
);
</script>

<style scoped>
.step-card {
  width: 100%;
  padding: 18px 22px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 22px;
}

/* Header */

.step-header {
  max-width: 660px;
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 10px;
}

.step-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  color: #6b63e8;

  background:
    linear-gradient(
      135deg,
      rgba(83, 104, 232, 0.12),
      rgba(122, 83, 224, 0.08)
    );

  border: 1px solid rgba(92, 90, 224, 0.12);
  border-radius: 12px;
}

.step-icon svg {
  width: 22px;
  height: 22px;
}

.step-heading {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #969ba5;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.step-heading h3 {
  margin: 0;
  color: #24272d;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 17px;
  line-height: 1.2;
}

.description {
  max-width: 660px;
  margin: 0 0 15px;

  color: #777c86;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.4;
}

/* Form */

.form-content {
  width: 100%;
  max-width: 620px;

  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field-block {
  width: 100%;
}

.field-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 6px;
}

.field-label {
  color: #40444c;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
}

.character-counter {
  color: #a1a5ad;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

/* Input */

.input-wrapper {
  width: 100%;
  height: 44px;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  background: #f5f6f8;
  border: 1px solid #dfe2e7;
  border-radius: 12px;

  box-shadow:
    inset 0 1px 2px
    rgba(24, 34, 55, 0.035);

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.input-wrapper:focus-within {
  background: #ffffff;
  border-color: #5b8dfd;

  box-shadow:
    0 0 0 3px
    rgba(91, 141, 253, 0.14);
}

.input-wrapper.invalid,
.textarea-wrapper.invalid {
  border-color: #e36a72;

  box-shadow:
    0 0 0 3px
    rgba(222, 75, 87, 0.09);
}

.input-wrapper.valid,
.textarea-wrapper.valid {
  border-color: rgba(52, 170, 111, 0.5);
}

.field-icon {
  width: 42px;
  height: 100%;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #8c929d;
}

.field-icon svg {
  width: 18px;
  height: 18px;
}

.input-wrapper:focus-within .field-icon {
  color: #4b83ec;
}

.text-input {
  min-width: 0;
  height: 100%;
  flex: 1;

  padding: 0 8px 0 0;

  box-sizing: border-box;

  color: #202329;
  background: transparent;

  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
}

.text-input::placeholder,
.textarea::placeholder {
  color: #a6aab2;
}

/* Textarea */

.textarea-wrapper {
  position: relative;

  width: 100%;
  min-height: 125px;

  box-sizing: border-box;

  background: #f5f6f8;
  border: 1px solid #dfe2e7;
  border-radius: 14px;

  box-shadow:
    inset 0 1px 2px
    rgba(24, 34, 55, 0.035);

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.textarea-wrapper:focus-within {
  background: #ffffff;
  border-color: #5b8dfd;

  box-shadow:
    0 0 0 3px
    rgba(91, 141, 253, 0.14);
}

.textarea {
  width: 100%;
  height: 125px;

  display: block;

  padding: 12px 42px 12px 14px;

  box-sizing: border-box;
  resize: vertical;

  color: #202329;
  background: transparent;

  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.45;
}

/* Status */

.valid-status,
.textarea-valid-status {
  width: 26px;
  height: 26px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #24965c;
  background: rgba(42, 174, 105, 0.1);
  border-radius: 8px;
}

.valid-status {
  margin-right: 9px;
}

.textarea-valid-status {
  position: absolute;
  top: 9px;
  right: 9px;
}

.valid-status svg,
.textarea-valid-status svg {
  width: 14px;
  height: 14px;
}

/* Messages */

.field-message {
  min-height: 16px;

  display: flex;
  align-items: flex-start;
  gap: 6px;

  margin: 5px 2px 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.35;
}

.hint {
  color: #9297a1;
}

.error {
  color: #ce4d58;
}

.message-icon {
  width: 15px;
  height: 15px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background: #db5964;
  border-radius: 50%;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 9px;
}

/* Animations */

.status-enter-active,
.status-leave-active,
.message-enter-active,
.message-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.message-enter-from,
.message-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

/* Dark theme */


/* 1366x768 and smaller */

@media (max-width: 1366px), (max-height: 768px) {
  .step-card {
    padding: 16px 20px;
    border-radius: 20px;
  }

  .step-header {
    margin-bottom: 8px;
  }

  .step-icon {
    width: 38px;
    height: 38px;
  }

  .step-icon svg {
    width: 20px;
    height: 20px;
  }

  .step-heading h3 {
    font-size: 16px;
  }

  .description {
    margin-bottom: 12px;
    font-size: 11px;
    line-height: 1.35;
  }

  .form-content {
    gap: 13px;
  }

  .field-heading {
    margin-bottom: 5px;
  }

  .input-wrapper {
    height: 42px;
  }

  .textarea-wrapper {
    min-height: 110px;
  }

  .textarea {
    height: 110px;
    min-height: 110px;
    padding: 11px 40px 11px 13px;
  }

  .field-message {
    min-height: 14px;
    margin-top: 4px;
    font-size: 9.5px;
  }
}

/* Mobile */

@media (max-width: 640px) {
  .step-card {
    padding: 18px 14px;
  }

  .step-heading h3 {
    font-size: 16px;
  }

  .form-content {
    max-width: none;
  }

  .textarea-wrapper {
    min-height: 120px;
  }

  .textarea {
    height: 120px;
    min-height: 120px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .input-wrapper,
  .textarea-wrapper,
  .status-enter-active,
  .status-leave-active,
  .message-enter-active,
  .message-leave-active {
    transition: none;
  }
}

html[data-theme="dark"] .step-card {
  background: #1c1c1e;
}

html[data-theme="dark"] .step-icon {
  color: #8f86ff;

  background:
    linear-gradient(
      135deg,
      rgba(83, 104, 232, 0.18),
      rgba(122, 83, 224, 0.12)
    );

  border-color: rgba(122, 110, 255, 0.2);
}

html[data-theme="dark"] .step-heading h3 {
  color: #f2f2f4;
}

html[data-theme="dark"] .step-label {
  color: #81858e;
}

html[data-theme="dark"] .description {
  color: #a7aab1;
}

html[data-theme="dark"] .field-label {
  color: #d4d6da;
}

html[data-theme="dark"] .character-counter {
  color: #7f838b;
}

html[data-theme="dark"] .input-wrapper,
html[data-theme="dark"] .textarea-wrapper {
  background: #292a2e;
  border-color: #414349;

  box-shadow:
    inset 0 1px 2px
    rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .input-wrapper:focus-within,
html[data-theme="dark"] .textarea-wrapper:focus-within {
  background: #2d2f34;
  border-color: #5989eb;

  box-shadow:
    0 0 0 3px
    rgba(70, 126, 229, 0.15),
    inset 0 1px 2px
    rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"] .input-wrapper.invalid,
html[data-theme="dark"] .textarea-wrapper.invalid {
  border-color: #e46d76;

  box-shadow:
    0 0 0 3px
    rgba(228, 77, 89, 0.13);
}

html[data-theme="dark"] .input-wrapper.valid,
html[data-theme="dark"] .textarea-wrapper.valid {
  border-color: rgba(61, 192, 126, 0.62);
}

html[data-theme="dark"] .field-icon {
  color: #7f848e;
}

html[data-theme="dark"] .input-wrapper:focus-within .field-icon {
  color: #67a2ff;
}

html[data-theme="dark"] .text-input,
html[data-theme="dark"] .textarea {
  color: #ffffff;
}

html[data-theme="dark"] .text-input::placeholder,
html[data-theme="dark"] .textarea::placeholder {
  color: #70747c;
}

html[data-theme="dark"] .valid-status,
html[data-theme="dark"] .textarea-valid-status {
  color: #51c989;
  background: rgba(61, 192, 126, 0.14);
}

html[data-theme="dark"] .hint {
  color: #7f838b;
}

html[data-theme="dark"] .error {
  color: #ff8b94;
}

html[data-theme="dark"] .message-icon {
  color: #ffffff;
  background: #df5b67;
}

</style>